/**
 * Phase F — Persistent public results view.
 * Route: /mishap-corrector/results/:analysis_id
 * Same rendering as Step 4, with print-friendly frame.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import MishapResultsView from "@/components/MishapResultsView";

export default function MishapResultsPage() {
  const { analysis_id } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/mishap/analyses/${analysis_id}`);
        setAnalysis(r.data);
      } catch {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [analysis_id]);

  if (loading) return <div className="min-h-screen bg-bone flex items-center justify-center text-ink-soft">Loading…</div>;
  if (notFound) return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center gap-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brass">404</div>
      <h1 className="font-serif text-3xl text-ink tracking-tight">Analysis not found</h1>
      <Link to="/mishap-corrector" className="text-[11px] uppercase tracking-[0.22em] text-ivy hover:text-brass">← Run a new analysis</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-bone">
      <SEO title={`Mishap Corrector — analysis for ${analysis.first_name || "student"} | Beyond Valencia`} noindex canonical={`/mishap-corrector/results/${analysis_id}`} />
      <style>{`@media print { header, footer, nav, .no-print { display: none !important; } body { background: #F5F1E8; } }`}</style>
      <section className="bg-ivy text-bone border-b border-ivy-light no-print">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Mishap Corrector · Result</div>
          <div className="mt-2 font-serif text-2xl tracking-tight">Analysis for {analysis.first_name}</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft mt-1 tabular-nums">Generated {(analysis.generated_at || "").slice(0, 10)}</div>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
        <MishapResultsView analysis={analysis} showPrintCTA={false} />
        <div className="mt-10 flex gap-4 no-print">
          <button onClick={() => window.print()} className="border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 px-5" data-testid="mishap-print-btn">Print / save PDF</button>
          <Link to="/mishap-corrector" className="text-[11px] uppercase tracking-[0.22em] text-stone hover:text-ivy self-center">← Run a new analysis</Link>
        </div>
      </section>
    </div>
  );
}
